import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useActiveGenerationBatch } from 'hooks/queries/schedules/useActiveGenerationBatch'
import { useCalendarLoadingState } from 'hooks/calendar/useCalendarHasLoadingEvents'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'

export interface V2GenerationStatus {
  isActive: boolean
  totalCount: number
  completedCount: number
  startedAtMs: number | null
}

export const V2_DEFAULT_STATUS: V2GenerationStatus = {
  isActive: false,
  totalCount: 0,
  completedCount: 0,
  startedAtMs: null,
}

const useActiveBatchScheduleId = (workspaceId: number | undefined): number | null => {
  const { data } = useQuery<number | null>(scheduleKeys.activeBatchId(workspaceId ?? 0), () => Promise.resolve(null), {
    enabled: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    initialData: null,
  })
  return data ?? null
}

export const useV2GenerationStatus = (enabled: boolean): V2GenerationStatus => {
  const workspaceId = useCurrentWorkspaceId()
  const cacheState = useCalendarLoadingState(enabled)
  const wsScheduleId = useActiveBatchScheduleId(workspaceId)

  const scheduleId = wsScheduleId ?? cacheState.scheduleId
  const { data: batch } = useActiveGenerationBatch(enabled ? scheduleId : null)

  const status = useMemo<V2GenerationStatus>(() => {
    if (!enabled) return V2_DEFAULT_STATUS

    return {
      isActive: !!batch?.is_active,
      totalCount: batch?.total_count ?? 0,
      completedCount: batch?.completed_count ?? 0,
      startedAtMs: batch?.started_at ? new Date(batch.started_at).getTime() : null,
    }
  }, [enabled, batch])

  return status
}
